import React, { useMemo } from 'react';
import { Icon } from '@tviklax/ui-components';
import styled from 'styled-components';
import { Document } from './types';
import {
  PeoplePanelContainer,
  PanelHeader,
  PanelTitle,
  PanelSubtitle,
  PanelContent,
  SectionGroup,
  SectionHeader,
  SectionIcon,
  SectionTitle,
  SectionBadge,
  PersonCard,
  PersonName,
  PersonRole,
  PersonMetaList,
  PersonMeta,
  PersonMetaLabel,
  PersonMetaValue,
  AuthorityItem,
  BlockingStakeholder,
  BlockingName,
  BlockingMeta,
  EmptyState,
} from './peoplePanelStyles';

interface PeoplePanelProps {
  documents: Document[];
  offsetTop?: number;
  onOpenMessaging?: (payload: { targetContactName?: string; targetContactEmail?: string; preferredSurface?: 'drawer' | 'widget' }) => void;
}

const InlineChatTrigger = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid #dbeafe;
  background: #eff6ff;
  color: #2563eb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #dbeafe;
  }
`;

const InlineCallTrigger = styled.a`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid #dcfce7;
  background: #f0fdf4;
  color: #16a34a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  text-decoration: none;

  &:hover {
    background: #dcfce7;
  }
`;

const DocumentOwner = ({ documents, onOpenMessaging }: { documents: Document[]; onOpenMessaging?: PeoplePanelProps['onOpenMessaging'] }) => {
  const owners = useMemo(() => {
    const grouped = new Map<string, {
      issuedBy: string;
      contactName?: string;
      email?: string;
      phone?: string;
      organization?: string;
      role?: string;
      country?: string;
      documents: Map<string, Document>;
    }>();

    documents.forEach(doc => {
      if (!doc.issuedBy) return;

      const existing = grouped.get(doc.issuedBy);
      if (existing) {
        existing.documents.set(doc.id, doc);
        return;
      }

      grouped.set(doc.issuedBy, {
        issuedBy: doc.issuedBy,
        contactName: doc.handledBy,
        email: doc.handledByEmail,
        phone: doc.handledByPhone,
        organization: doc.handledByOrganization,
        role: doc.handledByRole,
        country: doc.handledByCountry,
        documents: new Map([[doc.id, doc]]),
      });
    });

    return Array.from(grouped.values())
      .map((owner) => ({
        ...owner,
        documents: Array.from(owner.documents.values()),
        docCount: owner.documents.size,
      }))
      .sort((left, right) => right.docCount - left.docCount || left.issuedBy.localeCompare(right.issuedBy));
  }, [documents]);

  if (owners.length === 0) return null;

  return (
    <SectionGroup>
      <SectionHeader>
        <SectionIcon>📄</SectionIcon>
        <SectionTitle>Document Owner</SectionTitle>
        <SectionBadge>{owners.length}</SectionBadge>
      </SectionHeader>
      {owners.map(({ issuedBy, contactName, email, phone, organization, role, country, docCount }) => (
        <PersonCard key={issuedBy}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
            <PersonName style={{ marginBottom: 0 }}>{issuedBy}</PersonName>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {phone && (
                <InlineCallTrigger
                  href={`tel:${phone}`}
                  aria-label={`Call ${contactName || issuedBy}`}
                >
                  <Icon name="Phone" size={14} />
                </InlineCallTrigger>
              )}
              {(contactName || email) && (
                <InlineChatTrigger
                  aria-label={`Message ${contactName || issuedBy}`}
                  onClick={() => onOpenMessaging?.({ targetContactName: contactName || issuedBy, targetContactEmail: email, preferredSurface: 'drawer' })}
                >
                  <Icon name="Mail" size={14} />
                </InlineChatTrigger>
              )}
            </div>
          </div>
          {role && <PersonRole>{role}</PersonRole>}
          <PersonMetaList>
            {contactName && (
              <PersonMeta>
                <PersonMetaLabel>Contact:</PersonMetaLabel>
                <PersonMetaValue>{contactName}</PersonMetaValue>
              </PersonMeta>
            )}
            {organization && (
              <PersonMeta>
                <PersonMetaLabel>Organization:</PersonMetaLabel>
                <PersonMetaValue>{organization}</PersonMetaValue>
              </PersonMeta>
            )}
            {country && (
              <PersonMeta>
                <PersonMetaLabel>Country:</PersonMetaLabel>
                <PersonMetaValue>{country}</PersonMetaValue>
              </PersonMeta>
            )}
            {email && (
              <PersonMeta>
                <PersonMetaLabel>Email:</PersonMetaLabel>
                <PersonMetaValue>{email}</PersonMetaValue>
              </PersonMeta>
            )}
            {phone && (
              <PersonMeta>
                <PersonMetaLabel>Phone:</PersonMetaLabel>
                <PersonMetaValue>{phone}</PersonMetaValue>
              </PersonMeta>
            )}
            <PersonMeta>
              <PersonMetaLabel>Documents:</PersonMetaLabel>
              <PersonMetaValue>{docCount}</PersonMetaValue>
            </PersonMeta>
          </PersonMetaList>
        </PersonCard>
      ))}
    </SectionGroup>
  );
};

const DocumentHandlers = ({ documents, onOpenMessaging }: { documents: Document[]; onOpenMessaging?: PeoplePanelProps['onOpenMessaging'] }) => {
  const handlersByContact = useMemo(() => {
    const grouped = new Map<string, {
      id: string;
      name: string;
      email?: string;
      team?: string;
      documents: Map<string, Document>;
    }>();

    documents.forEach(doc => {
      if (!doc.handledBy) return;

      const contactKey = doc.contactId ? `contact:${doc.contactId}` : `name:${doc.handledBy}`;
      const existing = grouped.get(contactKey);
      if (existing) {
        existing.documents.set(doc.id, doc);
        return;
      }

      grouped.set(contactKey, {
        id: contactKey,
        name: doc.handledBy,
        email: doc.handledByEmail,
        team: doc.contactTeam,
        documents: new Map([[doc.id, doc]]),
      });
    });

    return Array.from(grouped.values())
      .map((handler) => ({
        ...handler,
        documents: Array.from(handler.documents.values()),
        docCount: handler.documents.size,
      }))
      .sort((left, right) => right.docCount - left.docCount || left.name.localeCompare(right.name));
  }, [documents]);

  if (handlersByContact.length === 0) return null;

  return (
    <SectionGroup>
      <SectionHeader>
        <SectionIcon>👥</SectionIcon>
        <SectionTitle>Handlers</SectionTitle>
        <SectionBadge>{handlersByContact.length}</SectionBadge>
      </SectionHeader>
      {handlersByContact.map(({ id, name, email, team, docCount }) => (
        <AuthorityItem key={id}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 500 }}>• {name}</span>
              <InlineChatTrigger
                aria-label={`Message ${name}`}
                onClick={() => onOpenMessaging?.({ targetContactName: name, targetContactEmail: email, preferredSurface: 'drawer' })}
              >
                <Icon name="Mail" size={14} />
              </InlineChatTrigger>
            </div>
            {(team || email) && (
              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                {team || 'Unassigned team'}{team && email ? ' · ' : ''}{email || ''}
              </span>
            )}
          </div>
          <span style={{ fontSize: '0.8rem', color: '#6b7280', marginLeft: '8px', whiteSpace: 'nowrap' }}>
            {docCount} doc{docCount !== 1 ? 's' : ''}
          </span>
        </AuthorityItem>
      ))}
    </SectionGroup>
  );
};

const BlockingStakeholders = ({ documents }: { documents: Document[] }) => {
  const blockingDocs = documents.filter(d => d.status === 'blocking');
  
  if (blockingDocs.length === 0) return null;

  const stakeholders = useMemo(() => {
    const grouped = new Map<string, typeof blockingDocs>();
    
    blockingDocs.forEach(doc => {
      const key = doc.issuedBy;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(doc);
    });

    return Array.from(grouped.entries()).map(([issuedBy, docs]) => {
      return {
        issuedBy,
        documents: docs,
      };
    });
  }, [blockingDocs]);

  return (
    <SectionGroup>
      <SectionHeader>
        <SectionIcon>🚨</SectionIcon>
        <SectionTitle>Blocking Stakeholders</SectionTitle>
        <SectionBadge style={{ background: '#fee2e2', color: '#991b1b' }}>
          {blockingDocs.length}
        </SectionBadge>
      </SectionHeader>
      {stakeholders.map(({ issuedBy, documents: docs }) => (
        <BlockingStakeholder key={issuedBy}>
          <BlockingName>⚠ {issuedBy}</BlockingName>
          <BlockingMeta>
            {docs.map(doc => (
              <div key={doc.id} style={{ marginBottom: '8px' }}>
                <div style={{ fontWeight: 500, color: '#1f2937' }}>
                  • {doc.name}
                </div>
                {doc.dueDate && (
                  <div style={{ marginLeft: 12, marginTop: 2, fontSize: '0.8rem', color: '#6b7280' }}>
                    Due: {new Date(doc.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                )}
                {doc.lastAction && (
                  <div style={{ marginLeft: 12, marginTop: 2, fontSize: '0.8rem', color: '#6b7280' }}>
                    Status: {doc.lastAction}
                  </div>
                )}
              </div>
            ))}
          </BlockingMeta>
        </BlockingStakeholder>
      ))}
    </SectionGroup>
  );
};

const PeoplePanel: React.FC<PeoplePanelProps> = ({ documents, offsetTop, onOpenMessaging }) => {
  const totalPeople = useMemo(() => {
    const people = new Set<string>();
    documents.forEach(doc => {
      if (doc.handledBy) {
        people.add(doc.handledBy);
      }
    });
    return people.size;
  }, [documents]);

  if (documents.length === 0) {
    return (
      <PeoplePanelContainer $offsetTop={offsetTop}>
        <PanelHeader>
          <PanelTitle>People Involved</PanelTitle>
        </PanelHeader>
        <PanelContent>
          <EmptyState>No documents selected</EmptyState>
        </PanelContent>
      </PeoplePanelContainer>
    );
  }

  return (
    <PeoplePanelContainer $offsetTop={offsetTop}>
      <PanelHeader>
        <PanelTitle>People Involved</PanelTitle>
        <PanelSubtitle>👥 {totalPeople} people across the selected documents</PanelSubtitle>
      </PanelHeader>
      <PanelContent>
        <DocumentOwner documents={documents} onOpenMessaging={onOpenMessaging} />
        <DocumentHandlers documents={documents} onOpenMessaging={onOpenMessaging} />
        <BlockingStakeholders documents={documents} />
      </PanelContent>
    </PeoplePanelContainer>
  );
};

export default PeoplePanel;
